import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, ArrowRight, MapPin, Mail, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', organization: '', practiceSize: '', phone: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', organization: '', practiceSize: '', phone: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div style={{ background: '#0a0a0a', minHeight: '100vh' }}>
      {/* Hero */}
      <section className="pt-32 pb-16">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-[640px] mx-auto px-4 text-center">
          <motion.p variants={fadeUp} className="text-[13px] font-medium tracking-[0.1em] uppercase" style={{ color: '#888' }}>Get in Touch</motion.p>
          <motion.h1 variants={fadeUp} className="text-[40px] md:text-[56px] text-white font-bold leading-[1.1] tracking-[-0.03em] mt-4">
            Let's talk about your practice.
          </motion.h1>
          <motion.p variants={fadeUp} className="text-lg text-[#aaa] leading-[1.6] max-w-[480px] mx-auto mt-4 font-normal">
            Whether you're exploring AriaOne or ready to deploy, we'd love to hear from you.
          </motion.p>
        </motion.div>
      </section>

      {/* Form + Info */}
      <section className="pb-24">
        <div className="max-w-[1080px] mx-auto px-4 flex flex-col lg:flex-row gap-12">
          {/* Left: Form */}
          <motion.div
            className="flex-1"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <div className="rounded-2xl p-8 md:p-10" style={{ background: '#111111', border: '0.5px solid #2a2a2a' }}>
              <h2 className="text-xl text-white font-bold mb-6">Request a Demo</h2>

              {status === 'success' ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <Send className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-white text-lg font-bold">Message sent!</h3>
                  <p className="text-[#aaa] text-sm mt-2 font-normal">We'll be in touch within 24 hours.</p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-6 text-white text-sm underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-[#888] text-xs font-medium uppercase tracking-wide block mb-2">Full Name *</label>
                      <input
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        className="w-full h-11 px-4 rounded-lg text-white text-sm bg-transparent outline-none transition-colors"
                        style={{ border: '0.5px solid #2a2a2a' }}
                        placeholder="Dr. Jane Smith"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="text-[#888] text-xs font-medium uppercase tracking-wide block mb-2">Email *</label>
                      <input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        className="w-full h-11 px-4 rounded-lg text-white text-sm bg-transparent outline-none transition-colors"
                        style={{ border: '0.5px solid #2a2a2a' }}
                        placeholder="jane@practice.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-[#888] text-xs font-medium uppercase tracking-wide block mb-2">Organization</label>
                      <input
                        name="organization"
                        value={form.organization}
                        onChange={handleChange}
                        className="w-full h-11 px-4 rounded-lg text-white text-sm bg-transparent outline-none transition-colors"
                        style={{ border: '0.5px solid #2a2a2a' }}
                        placeholder="City Medical Group"
                        data-testid="contact-org"
                      />
                    </div>
                    <div>
                      <label className="text-[#888] text-xs font-medium uppercase tracking-wide block mb-2">Practice Size</label>
                      <select
                        name="practiceSize"
                        value={form.practiceSize}
                        onChange={handleChange}
                        className="w-full h-11 px-4 rounded-lg text-sm bg-transparent outline-none appearance-none cursor-pointer"
                        style={{ border: '0.5px solid #2a2a2a', color: form.practiceSize ? '#ffffff' : '#555' }}
                        data-testid="contact-size"
                      >
                        <option value="" style={{ background: '#111', color: '#555' }}>Select size</option>
                        <option value="solo" style={{ background: '#111', color: '#fff' }}>Solo Practice</option>
                        <option value="2-5" style={{ background: '#111', color: '#fff' }}>2–5 Providers</option>
                        <option value="6-20" style={{ background: '#111', color: '#fff' }}>6–20 Providers</option>
                        <option value="21-50" style={{ background: '#111', color: '#fff' }}>21–50 Providers</option>
                        <option value="50+" style={{ background: '#111', color: '#fff' }}>50+ Providers</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-[#888] text-xs font-medium uppercase tracking-wide block mb-2">Phone (optional)</label>
                    <input
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      className="w-full h-11 px-4 rounded-lg text-white text-sm bg-transparent outline-none transition-colors"
                      style={{ border: '0.5px solid #2a2a2a' }}
                      placeholder="(555) 123-4567"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="text-[#888] text-xs font-medium uppercase tracking-wide block mb-2">Message *</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 rounded-lg text-white text-sm bg-transparent outline-none resize-none transition-colors"
                      style={{ border: '0.5px solid #2a2a2a' }}
                      placeholder="Tell us about your practice and what you're looking for..."
                      data-testid="contact-message"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="h-11 px-8 rounded-lg text-black text-sm font-medium flex items-center gap-2 transition-opacity"
                    style={{ background: '#ffffff', opacity: status === 'sending' ? 0.6 : 1 }}
                    data-testid="contact-submit"
                  >
                    {status === 'sending' ? 'Sending...' : 'Send Message'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  {status === 'error' && (
                    <p className="text-red-400 text-sm mt-2" data-testid="contact-error">Something went wrong. Please try again.</p>
                  )}
                </form>
              )}
            </div>
          </motion.div>

          {/* Right: Info cards */}
          <motion.div
            className="w-full lg:w-[340px] space-y-5"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            {[
              { icon: Mail, label: 'Email Us', value: 'hello@ariaone.ai', sub: 'We reply within 24 hours' },
              { icon: Phone, label: 'Call Us', value: '(888) 274-2663', sub: 'Mon–Fri 8am–6pm ET' },
              { icon: MapPin, label: 'Visit Us', value: 'San Francisco, CA', sub: 'By appointment only' },
            ].map((item, i) => (
              <div key={i} className="rounded-xl p-6" style={{ background: '#111111', border: '0.5px solid #2a2a2a' }} data-testid={`contact-info-${i}`}>
                <item.icon className="w-5 h-5 mb-3" style={{ color: '#888' }} />
                <p className="text-[#888] text-xs font-medium uppercase tracking-wide">{item.label}</p>
                <p className="text-white text-lg font-bold mt-1">{item.value}</p>
                <p className="text-[#555] text-sm mt-1 font-normal">{item.sub}</p>
              </div>
            ))}

            <div className="rounded-xl p-6" style={{ background: '#111111', border: '0.5px solid #2a2a2a' }}>
              <p className="text-[#888] text-xs font-medium uppercase tracking-wide">For Existing Customers</p>
              <p className="text-white text-base font-bold mt-2">Need support?</p>
              <p className="text-[#aaa] text-sm mt-1 font-normal">Access your dashboard at <span className="text-white">app.ariaone.ai</span> or email support@ariaone.ai</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
